import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for detecting stale pages and auto-reloading after idle period
 * Implements US-DS-11: Stale Page Protection
 *
 * @param {Object} options - Configuration options
 * @param {number} options.idleThreshold - Idle time in milliseconds before reload (default: 1 hour)
 * @param {Object} options.preserveState - State to preserve across reload
 * @returns {Object} Page staleness info
 */
export function useStalePageProtection({
  idleThreshold = 60 * 60 * 1000, // 1 hour
  preserveState = {}
} = {}) {
  const pageLoadTime = useRef(Date.now());
  const lastInteractionTime = useRef(Date.now());
  const checkIntervalRef = useRef(null);

  // Update last interaction time on user activity
  const trackInteraction = useCallback(() => {
    lastInteractionTime.current = Date.now();
  }, []);

  // Check if page is stale
  const checkStaleness = useCallback(() => {
    const now = Date.now();
    const idleTime = now - lastInteractionTime.current;

    if (idleTime >= idleThreshold) {
      // Preserve state before reload
      const stateToPreserve = {
        timestamp: now,
        pageLoadTime: pageLoadTime.current,
        ...preserveState
      };

      sessionStorage.setItem('pageStateBeforeReload', JSON.stringify(stateToPreserve));

      // Reload the page
      window.location.reload();
    }
  }, [idleThreshold, preserveState]);

  // Set up event listeners and interval
  useEffect(() => {
    // Track user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      window.addEventListener(event, trackInteraction, { passive: true });
    });

    // Check for staleness every 30 seconds
    checkIntervalRef.current = setInterval(checkStaleness, 30000);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, trackInteraction);
      });

      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [trackInteraction, checkStaleness]);

  // Restore state after reload (if any)
  const getRestoredState = useCallback(() => {
    try {
      const savedState = sessionStorage.getItem('pageStateBeforeReload');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Clear it after reading
        sessionStorage.removeItem('pageStateBeforeReload');
        return parsed;
      }
    } catch (err) {
      console.error('Failed to restore page state:', err);
    }
    return null;
  }, []);

  return {
    pageLoadTime: pageLoadTime.current,
    lastInteractionTime: lastInteractionTime.current,
    getRestoredState,
    trackInteraction, // Manual interaction tracking if needed
  };
}

export default useStalePageProtection;
